import React from 'react';
import { Link } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { getApi, postApi, putApi } from '../../services/apiServices';
import { toast } from 'react-toastify';

function ContentManagement() {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        question: '',
        step: '',
        characterLimit: ''
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [editFormData, setEditFormData] = useState({
        question: '',
        step: '',
        characterLimit: ''
    });
    const [isEditing, setIsEditing] = useState(false);

    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = new URLSearchParams();
            if (searchTerm.trim()) {
                params.append('search', searchTerm.trim());
            }
            const url = searchTerm.trim() ? `/question?${params.toString()}` : '/question';
            const response = await getApi(url);
            if (response?.statusCode === 200) {
                setQuestions(response.data || []);
            } else {
                setError(response?.message || 'Failed to fetch questions');
                console.error('Failed to fetch questions:', response?.message);
            }
        } catch (error) {
            setError('Error fetching questions. Please try again.');
            console.error('Error fetching questions:', error);
        } finally {
            setLoading(false);
        }
    }, [searchTerm]);

    
    useEffect(() => {
        if (searchTerm.trim() !== '') {
            const timeoutId = setTimeout(() => {
                fetchQuestions();
            }, 500);
            return () => clearTimeout(timeoutId);
        }
       
        fetchQuestions();
    }, [searchTerm, fetchQuestions]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.question.trim()) {
            toast.error('Question text is required');
            return;
        }

        if (!formData.step) {
            toast.error('Please select a step');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await postApi('/question', {
                question: formData.question.trim(),
                step: parseInt(formData.step),
                characterLimit: formData.characterLimit ? parseInt(formData.characterLimit) : null
            });

            if (response?.statusCode === 200) {
                toast.success('Question added successfully!');
                setIsAddModalOpen(false);
                setFormData({
                    question: '',
                    step: '',
                    characterLimit: ''
                });
                await fetchQuestions(); 
            } else {
                toast.error(response?.message || 'Failed to add question');
            }
        } catch (error) {
            toast.error('Error adding question. Please try again.');
            console.error('Error adding question:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openAddModal = () => {
        setIsAddModalOpen(true);
    };

    const closeAddModal = () => {
        setIsAddModalOpen(false);
        setFormData({
            question: '',
            step: '',
            characterLimit: ''
        });
    };

    const openEditModal = (question) => {
        setEditingQuestion(question);
        setEditFormData({
            question: question.question,
            step: question.step.toString(),
            characterLimit: question.characterLimit ? question.characterLimit.toString() : ''
        });
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingQuestion(null);
        setEditFormData({
            question: '',
            step: '',
            characterLimit: ''
        });
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        
        if (!editFormData.question.trim()) {
            toast.error('Question text is required');
            return;
        }

        if (!editFormData.step) {
            toast.error('Please select a step');
            return;
        }

        setIsEditing(true);
        try {
            const response = await putApi('/edit-question', {
                question_id: editingQuestion.id,
                question: editFormData.question.trim()
            });

            if (response?.statusCode === 200) {
                toast.success('Question updated successfully!');
                closeEditModal();
                await fetchQuestions();
            } else {
                toast.error(response?.message || 'Failed to update question');
            }
        } catch (error) {
            toast.error('Error updating question. Please try again.');
            console.error('Error updating question:', error);
        } finally {
            setIsEditing(false);
        }
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const stepOptions = Array.from({ length: 15 }, (_, i) => i + 1);
    
    const characterLimitOptions = [100, 150, 200, 250, 300, 500];

    return (
        <>
            <div className="right-section">
                <div className="customer-top">
                    <div className="customer-top-left">
                        <div className="cont-mang-haed">
                            <h3>Manage Profiling Questions (Dating Resume)</h3>
                            <h4>
                                Add, edit, delete, and organize the 15 curated questions shown to users during profile
                                setup.
                            </h4>
                        </div>
                    </div>
                    <div className="customer-top-right">
                        <input 
                            type="text" 
                            className="search-input" 
                            placeholder="Search questions" 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading && (
                    <div className="loader-overlay">
                        <span className="loader"></span>
                    </div>
                )}

                {error && (
                    <div className="alert alert-danger" role="alert">
                        {error}
                    </div>
                )}

                <div className="customer-tablle-main content-mangement-table">
                    <table className="table">
                        <thead>
                            <tr>
                                <th className="sr-no" scope="col">#</th>
                                <th scope="col" className="test">Question Text</th>
                                <th scope="col" className="step">Step</th>
                                <th scope="col" className="status">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-4">
                                        <div className="text-muted">Loading questions...</div>
                                    </td>
                                </tr>
                            ) : error ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-4 text-danger">{error}</td>
                                </tr>
                            ) : questions.length === 0 ? (
                                <tr>
                                    <td colSpan="4" className="text-center py-4">
                                        {searchTerm ? 'No questions found matching your search.' : 'No questions available.'}
                                    </td>
                                </tr>
                            ) : (
                                questions.map((question, index) => (
                                    <tr key={question.id}>
                                        <td className="sr-no">{String(index + 1).padStart(2, '0')}</td>
                                        <td className="test">
                                            <p>{question.question}</p>
                                        </td>
                                        <td className="step">{String(question.step).padStart(2, '0')}</td>
                                        <td className="action">
                                            <div className="view-image">
                                                <button className="btn table-btn" onClick={() => openEditModal(question)}>
                                                    <img src={window.getPublicUrl('/images/edit-icon.svg')} alt="Edit" />
                                                </button>
                                                {/* <button className="btn table-btn">
                                                    <img src={window.getPublicUrl('/images/block.svg')} alt="Delete" />
                                                </button> */}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {isAddModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="addQuestionTitle" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered send-email-modal" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="addQuestionTitle">Add Question</h5>
                                <button type="button" className="close" onClick={closeAddModal} aria-label="Close">
                                    <img src={window.getPublicUrl('/images/close-icon.svg')} alt="" />
                                </button>
                            </div>
                            <form onSubmit={handleSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <div className="form-group">
                                                <label>Question Text *</label>
                                                <textarea
                                                    className="form-control"
                                                    placeholder="What values matter most to you in a relationship?"
                                                    name="question"
                                                    value={formData.question}
                                                    onChange={handleInputChange}
                                                    rows="3"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="form-group">
                                                <label>Show in Step (1-15) *</label>
                                                <select 
                                                    className="form-control"
                                                    name="step"
                                                    value={formData.step}
                                                    onChange={handleInputChange}
                                                    required
                                                >
                                                    <option value="">Select Step</option>
                                                    {stepOptions.map(step => (
                                                        <option key={step} value={step}>
                                                            {String(step).padStart(2, '0')}
                                                        </option>
                                                    ))}
                                                </select>   
                                            </div>
                                        </div>
                                        {/* <div className="col-sm-6">
                                            <div className="form-group">
                                                <label>Character Limit Allowed</label>
                                                <select 
                                                    className="form-control"
                                                    name="characterLimit"
                                                    value={formData.characterLimit}
                                                    onChange={handleInputChange}
                                                >
                                                    <option value="">No Limit</option>
                                                    {characterLimitOptions.map(limit => (
                                                        <option key={limit} value={limit}>
                                                            {limit}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-cancel" onClick={closeAddModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-save" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Adding...
                                            </>
                                        ) : (
                                            "Save"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Question Modal */}
            {isEditModalOpen && (
                <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="editQuestionTitle" aria-hidden="true">
                    <div className="modal-dialog modal-dialog-centered send-email-modal" role="document">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title" id="editQuestionTitle">Edit Question</h5>
                                <button type="button" className="close" onClick={closeEditModal} aria-label="Close">
                                    <img src={window.getPublicUrl('/images/close-icon.svg')} alt="" />
                                </button>
                            </div>
                            <form onSubmit={handleEditSubmit}>
                                <div className="modal-body">
                                    <div className="row">
                                        <div className="col-sm-12">
                                            <div className="form-group">
                                                <label>Question Text *</label>
                                                <textarea
                                                    className="form-control"
                                                    placeholder="What values matter most to you in a relationship?"
                                                    name="question"
                                                    value={editFormData.question}
                                                    onChange={handleEditInputChange}
                                                    rows="3"
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-sm-6">
                                            <div className="form-group">
                                                <label>Show in Step (1-15) *</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    name="step"
                                                    value={editFormData.step ? String(editFormData.step).padStart(2, '0') : ''}
                                                    disabled
                                                    readOnly
                                                />
                                            </div>
                                        </div>
                                        {/* <div className="col-sm-6">
                                            <div className="form-group">
                                                <label>Character Limit Allowed</label>
                                                <select 
                                                    className="form-control"
                                                    name="characterLimit"
                                                    value={editFormData.characterLimit}
                                                    onChange={handleEditInputChange}
                                                >
                                                    <option value="">No Limit</option>
                                                    {characterLimitOptions.map(limit => (
                                                        <option key={limit} value={limit}>
                                                            {limit}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div> */}
                                    </div>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-cancel" onClick={closeEditModal}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="btn btn-save" disabled={isEditing}>
                                        {isEditing ? (
                                            <>
                                                <span
                                                    className="spinner-border spinner-border-sm me-2"
                                                    role="status"
                                                    aria-hidden="true"
                                                ></span>
                                                Updating...
                                            </>
                                        ) : (
                                            "Update"
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Backdrop */}
            {(isAddModalOpen || isEditModalOpen) && (
                <div 
                    className="modal-backdrop fade show" 
                    style={{ display: 'block' }}
                    onClick={isAddModalOpen ? closeAddModal : closeEditModal}
                ></div>
            )}
        </>
    );
}

export default ContentManagement;